import React, { useCallback, useEffect, useMemo, useState } from 'react';
import crmClient from './crmClient';

const EMPTY_POST = {
  title: '',
  body: '',
  media_type: 'none',
  media_url: '',
  link_url: '',
  target_account_ids: [],
  scheduled_for: '',
  ai_generated: false,
  generation_cost_cents: 0,
  capture_lead: false
};

const STATUS_LABEL = {
  draft: 'Draft',
  needs_review: 'Needs review',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  partially_published: 'Partial',
  failed: 'Failed',
  cancelled: 'Cancelled'
};

const platformLabel = (platforms, key) => {
  const found = (platforms || []).find(p => p.key === key);
  return found ? found.label : key;
};

// Format a generation cost (in cents) as dollars or cents for the posts table.
const formatCost = cents => {
  const n = Number(cents);
  if (!n || n <= 0) return '—';
  if (n >= 100) return '$' + (n / 100).toFixed(2);
  return n.toFixed(n < 1 ? 3 : 2) + '¢';
};

// Build a link to the live post from the stored per-platform publish result.
const livePermalink = post => {
  const results = (post && post.platform_results) || {};
  for (const r of Object.values(results)) {
    if (!r || !r.external_post_id) continue;
    if (r.permalink) return r.permalink;
    const id = String(r.external_post_id);
    if (r.platform === 'facebook' && id.includes('_')) {
      const [page, p] = id.split('_');
      return `https://www.facebook.com/${page}/posts/${p}`;
    }
    if (r.platform === 'instagram') return `https://www.instagram.com/`;
    return `https://www.facebook.com/${id}`;
  }
  return null;
};

const SocialView = ({ isAdmin }) => {
  const [meta, setMeta] = useState(null);
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [accountForm, setAccountForm] = useState({ platform: 'facebook', external_id: '', display_name: '', access_token: '' });
  const [fbToken, setFbToken] = useState('');
  const [preview, setPreview] = useState(null);
  const [gen, setGen] = useState({ pillar: 'education', topic: '', instructions: '', platform: 'facebook', media_type: 'image' });
  const [genBusy, setGenBusy] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleAccountIds, setScheduleAccountIds] = useState([]);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishAccountIds, setPublishAccountIds] = useState([]);
  const [publishError, setPublishError] = useState(null);
  const [analyticsPostId, setAnalyticsPostId] = useState(null);
  const [edit, setEdit] = useState({ body: '', media_url: '', media_type: 'none', image_prompt: '', cost: 0 });
  const [editBusy, setEditBusy] = useState(false);

  const accounts = (meta && meta.accounts) || [];
  const connectedAccounts = accounts.filter(a => a.is_active);

  const load = useCallback(() => {
    crmClient.socialMeta().then(setMeta).catch(() => setMeta({ platforms: [], accounts: [] }));
    crmClient.listSocialPosts().then(payload => setPosts((payload && payload.posts) || [])).catch(() => {});
    crmClient.socialAnalytics(analyticsPostId ? { post_id: analyticsPostId } : undefined).then(setAnalytics).catch(() => {});
  }, [analyticsPostId]);

  useEffect(() => {
    load();
  }, [load]);

  // Seed the editable preview fields whenever a post is opened.
  useEffect(() => {
    if (preview) {
      setEdit({
        body: preview.body || '',
        media_url: preview.media_url || '',
        media_type: preview.media_type || 'none',
        image_prompt: (preview.generation_meta && preview.generation_meta.image_prompt) || preview.title || '',
        cost: 0
      });
    }
  }, [preview]);

  // Surface the OAuth connect result after Facebook redirects back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('social_connected')) {
      const ok = params.get('social_connected') === '1';
      setMessage({ text: ok ? 'Facebook/Instagram connected.' : 'Connection failed or was cancelled.', isError: !ok });
      params.delete('social_connected');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
      setTimeout(() => setMessage(null), 6000);
    }
  }, []);

  const connectFacebook = async () => {
    setBusy(true);
    try {
      const res = await crmClient.startFacebookConnect();
      if (res && res.url) {
        window.location.href = res.url;
        return;
      }
      notify('Facebook connect is not configured yet.', true);
    } catch (err) {
      notify('Could not start Facebook connect.', true);
    } finally {
      setBusy(false);
    }
  };

  const connectFacebookByToken = async () => {
    if (!fbToken.trim()) return notify('Paste a Facebook token first.', true);
    setBusy(true);
    try {
      const res = await crmClient.connectFacebookToken(fbToken.trim());
      const n = (res && res.connected && res.connected.length) || 0;
      notify(n ? `Connected ${n} account(s).` : 'No pages found for that token.', !n);
      setFbToken('');
      load();
    } catch (err) {
      notify((err && err.response && err.response.data && err.response.data.message) || 'Token connect failed.', true);
    } finally {
      setBusy(false);
    }
  };

  const toggleTarget = id => {
    setForm(prev => {
      const has = prev.target_account_ids.includes(id);
      return {
        ...prev,
        target_account_ids: has
          ? prev.target_account_ids.filter(x => x !== id)
          : [...prev.target_account_ids, id]
      };
    });
  };

  const buildBody = () => ({
    title: form.title || null,
    body: form.body,
    media_type: form.media_type,
    media_url: form.media_url || null,
    link_url: form.link_url || null,
    target_account_ids: form.target_account_ids,
    ai_generated: Boolean(form.ai_generated),
    generation_cost_cents: form.generation_cost_cents || 0,
    capture_lead: Boolean(form.capture_lead)
  });

  // Generate copy AND media together, save as a draft in the Posts section for
  // review, and open the preview. One action, human-approved before publishing.
  const generateDraft = async () => {
    setGenBusy(true);
    try {
      const content = await crmClient.generateSocialContent({
        platforms: [gen.platform],
        pillar: gen.pillar,
        topic: gen.topic,
        instructions: gen.instructions
      });
      const caption = (content && content.captions && content.captions[gen.platform]) || '';
      if (!caption) return notify('No copy came back. Add a topic and try again.', true);

      let cost = content.cost_cents || 0;
      let mediaType = 'none';
      let mediaUrl = null;
      let videoJobId = null;
      const genMeta = { image_prompt: content.image_prompt || null, pillar: gen.pillar, topic: gen.topic || null };

      if (gen.media_type === 'image') {
        const img = await crmClient.generateSocialImage({
          subject: gen.topic || content.image_prompt,
          instructions: content.image_prompt || gen.instructions,
          filename: gen.topic || 'gophr-social'
        });
        if (img && img.url) {
          mediaType = 'image';
          mediaUrl = img.url;
          cost += img.cost_cents || 0;
        } else {
          notify('Copy ready, but the image did not generate. Saved as text draft.', true);
        }
      } else if (gen.media_type === 'video') {
        // Storyboard drives the preview shot list; then start a real render.
        const sb = await crmClient.generateSocialStoryboard({ topic: gen.topic, instructions: gen.instructions });
        mediaType = 'video';
        cost += (sb && sb.cost_cents) || 0;
        genMeta.storyboard = sb || null;
        try {
          const vid = await crmClient.generateSocialVideo({ topic: gen.topic, instructions: gen.instructions, storyboard: sb });
          if (vid && vid.job_id) {
            videoJobId = vid.job_id;
            genMeta.video_job_id = vid.job_id;
            cost += vid.cost_cents || 0;
          }
        } catch (e) {
          notify('Copy + storyboard ready. Video render could not start; it stays a storyboard for review.', true);
        }
      }

      const targets = connectedAccounts
        .filter(a => a.platform === gen.platform)
        .map(a => a.crm_social_account_id);

      const payload = await crmClient.createSocialPost({
        title: gen.topic || caption.slice(0, 48),
        body: caption,
        media_type: mediaType,
        media_url: mediaUrl,
        target_account_ids: targets,
        ai_generated: true,
        generation_cost_cents: cost,
        generation_meta: genMeta,
        capture_lead: Boolean(gen.capture_lead)
      });
      const post = payload && payload.post;
      await load();
      const flags = (content.lint_flags || []).length;
      notify(
        `Draft created in Posts for review${flags ? ` (${flags} item(s) flagged)` : ''}.` +
        (videoJobId ? ' Video is rendering and will attach automatically in a minute or two.' : '')
      );
      if (post) setPreview(post);
      if (post && videoJobId) pollVideoForPost(post.crm_social_post_id, videoJobId);
    } catch (err) {
      notify((err && err.response && err.response.data && err.response.data.message) || 'Generation failed.', true);
    } finally {
      setGenBusy(false);
    }
  };

  // Poll a rendering video; when ready, refresh so the mp4 shows in the preview.
  // The backend cron also finalizes it, so this is just for immediacy.
  const pollVideoForPost = (postId, jobId) => {
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await crmClient.pollSocialVideo(jobId, postId);
        if (r && r.status === 'completed' && r.url) {
          await load();
          setPreview(prev => (prev && prev.crm_social_post_id === postId ? { ...prev, media_url: r.url } : prev));
          notify('Video render finished and attached.');
          return;
        }
        if (r && r.status === 'failed') {
          notify('Video render failed. The draft keeps its copy and storyboard.', true);
          return;
        }
      } catch (e) { /* keep trying */ }
      if (tries < 25) setTimeout(tick, 12000);
    };
    setTimeout(tick, 12000);
  };

  // Top up the auto-generated draft buffer (admin).
  const runAutogen = async () => {
    setGenBusy(true);
    try {
      const s = await crmClient.runSocialAutogen({});
      if (s && s.ok === false) return notify(s.error || 'Auto-generate is not available.', true);
      await load();
      notify(`Auto-generated ${(s && s.created) || 0} draft(s) into Posts for review.`);
    } catch (err) {
      notify((err && err.response && err.response.data && err.response.data.message) || 'Auto-generate failed.', true);
    } finally {
      setGenBusy(false);
    }
  };

  const notify = (text, isError) => {
    setMessage({ text, isError: Boolean(isError) });
    setTimeout(() => setMessage(null), 5000);
  };

  const resetForm = () => setForm(EMPTY_POST);

  const saveDraft = async () => {
    if (!form.body.trim()) return notify('Add some copy first.', true);
    setBusy(true);
    try {
      await crmClient.createSocialPost(buildBody());
      notify('Draft saved.');
      resetForm();
      load();
    } catch (err) {
      notify('Could not save draft.', true);
    } finally {
      setBusy(false);
    }
  };

  const publishNow = async () => {
    if (!form.body.trim()) return notify('Add some copy first.', true);
    if (!form.target_account_ids.length) return notify('Select at least one platform.', true);
    setBusy(true);
    try {
      const payload = await crmClient.createSocialPost(buildBody());
      const post = payload && payload.post;
      if (post) {
        const res = await crmClient.publishSocialPost(post.crm_social_post_id);
        const result = res && res.result;
        if (result && result.ok) notify('Published.');
        else notify('Saved, but no platform accepted it yet (connect a token).', true);
      }
      resetForm();
      load();
    } catch (err) {
      notify('Publish failed.', true);
    } finally {
      setBusy(false);
    }
  };

  const schedulePost = async () => {
    if (!form.body.trim()) return notify('Add some copy first.', true);
    if (!form.target_account_ids.length) return notify('Select at least one platform.', true);
    if (!form.scheduled_for) return notify('Pick a date and time.', true);
    setBusy(true);
    try {
      const payload = await crmClient.createSocialPost(buildBody());
      const post = payload && payload.post;
      if (post) {
        const res = await crmClient.scheduleSocialPost(post.crm_social_post_id, {
          scheduled_for: new Date(form.scheduled_for).toISOString(),
          reviewed: form.media_type !== 'video'
        });
        notify((res && res.note) || 'Scheduled.');
      }
      resetForm();
      load();
    } catch (err) {
      notify('Could not schedule.', true);
    } finally {
      setBusy(false);
    }
  };

  const publishExisting = async id => {
    setBusy(true);
    try {
      await crmClient.publishSocialPost(id);
      notify('Publish attempted.');
      load();
    } catch (err) {
      notify('Publish failed.', true);
    } finally {
      setBusy(false);
    }
  };

  // Publish from the Posts section (or preview) after choosing platform(s).
  const openPublish = post => {
    setPublishTarget(post);
    setPublishAccountIds(
      Array.isArray(post.target_account_ids) && post.target_account_ids.length
        ? post.target_account_ids
        : connectedAccounts.map(a => a.crm_social_account_id)
    );
    setPublishError(null);
    setPreview(null);
  };
  const confirmPublish = async () => {
    if (!publishTarget) return;
    setPublishError(null);
    if (!publishAccountIds.length) { setPublishError('Choose at least one platform to publish to.'); return; }
    setBusy(true);
    try {
      const saved = publishTarget.target_account_ids || [];
      if (publishAccountIds.length !== saved.length || publishAccountIds.some(id => !saved.includes(id))) {
        await crmClient.updateSocialPost(publishTarget.crm_social_post_id, { target_account_ids: publishAccountIds });
      }
      const res = await crmClient.publishSocialPost(publishTarget.crm_social_post_id);
      const result = res && res.result;
      if (result && result.ok) notify('Published.');
      else notify('Published, but a platform did not accept it (check tokens / media).', true);
      setPublishTarget(null);
      load();
    } catch (err) {
      setPublishError((err && err.response && err.response.data && err.response.data.message) || 'Publish failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // Regenerate the preview image from an editable prompt.
  const regenPreviewImage = async () => {
    setEditBusy(true);
    try {
      const res = await crmClient.generateSocialImage({
        subject: edit.image_prompt || (edit.body || '').slice(0, 120),
        instructions: edit.image_prompt,
        filename: 'regen'
      });
      if (res && res.url) {
        setEdit(prev => ({ ...prev, media_url: res.url, media_type: 'image', cost: prev.cost + (res.cost_cents || 0) }));
        notify('New image generated. Save to keep it.');
      } else {
        notify('No image came back.', true);
      }
    } catch (err) {
      notify((err && err.response && err.response.data && err.response.data.message) || 'Image regenerate failed.', true);
    } finally {
      setEditBusy(false);
    }
  };

  // Save edits made in the preview (copy + regenerated media) to the post.
  const savePreviewEdits = async () => {
    if (!preview) return;
    setEditBusy(true);
    try {
      const body = { body: edit.body, media_url: edit.media_url || null, media_type: edit.media_type };
      if (edit.cost > 0) body.add_generation_cost_cents = edit.cost;
      const res = await crmClient.updateSocialPost(preview.crm_social_post_id, body);
      notify('Changes saved.');
      await load();
      if (res && res.post) setPreview(res.post);
    } catch (err) {
      notify('Could not save changes.', true);
    } finally {
      setEditBusy(false);
    }
  };

  // Schedule an existing post from the Posts section (or the preview).
  const openSchedule = post => {
    setScheduleTarget(post);
    setScheduleAt('');
    setScheduleError(null);
    setScheduleAccountIds(Array.isArray(post.target_account_ids) ? post.target_account_ids : []);
    setPreview(null);
  };
  const confirmSchedule = async () => {
    if (!scheduleTarget) return;
    setScheduleError(null);
    if (!scheduleAt) { setScheduleError('Pick a date and time first.'); return; }
    const targets = scheduleAccountIds.length ? scheduleAccountIds : (scheduleTarget.target_account_ids || []);
    if (!targets.length) { setScheduleError('Choose at least one platform to post to.'); return; }
    setBusy(true);
    try {
      // If the chosen platforms differ from what's saved, update the post first.
      const saved = scheduleTarget.target_account_ids || [];
      if (targets.length !== saved.length || targets.some(id => !saved.includes(id))) {
        await crmClient.updateSocialPost(scheduleTarget.crm_social_post_id, { target_account_ids: targets });
      }
      const res = await crmClient.scheduleSocialPost(scheduleTarget.crm_social_post_id, {
        scheduled_for: new Date(scheduleAt).toISOString(),
        reviewed: scheduleTarget.media_type !== 'video'
      });
      notify((res && res.note) || 'Scheduled.');
      setScheduleTarget(null);
      load();
    } catch (err) {
      setScheduleError((err && err.response && err.response.data && err.response.data.message) || 'Could not schedule. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const deletePost = async id => {
    setBusy(true);
    try {
      await crmClient.deleteSocialPost(id);
      load();
    } catch (err) {
      notify('Could not delete.', true);
    } finally {
      setBusy(false);
    }
  };

  const connectAccount = async () => {
    if (!accountForm.external_id) return notify('Enter the page / organization id.', true);
    setBusy(true);
    try {
      await crmClient.connectSocialAccount(accountForm);
      notify('Account saved.');
      setAccountForm({ platform: 'facebook', external_id: '', display_name: '', access_token: '' });
      load();
    } catch (err) {
      notify('Could not save account.', true);
    } finally {
      setBusy(false);
    }
  };

  const removeAccount = async id => {
    setBusy(true);
    try {
      await crmClient.removeSocialAccount(id);
      load();
    } catch (err) {
      notify('Could not remove account.', true);
    } finally {
      setBusy(false);
    }
  };

  const platforms = (meta && meta.platforms) || [];
  const charCount = form.body.length;

  const accountName = useMemo(() => {
    const map = {};
    accounts.forEach(a => {
      map[a.crm_social_account_id] = a.display_name || `${platformLabel(platforms, a.platform)} (${a.external_id})`;
    });
    return map;
  }, [accounts, platforms]);

  return (
    <div className="crm-social">
      {message && (
        <div className={`crm-social-toast${message.isError ? ' crm-social-toast--error' : ''}`}>{message.text}</div>
      )}

      {preview && (
        <div className="crm-social-preview-overlay" onClick={() => setPreview(null)}>
          <div className="crm-social-preview" onClick={e => e.stopPropagation()}>
            <div className="crm-social-preview-head">
              <strong>Post preview</strong>
              <button className="crm-icon-btn" onClick={() => setPreview(null)}>✕</button>
            </div>
            <div className="crm-social-fbcard">
              <div className="crm-social-fbcard-head">
                <img className="crm-social-fbcard-avatar" src="https://gophrapp.com/wp-content/uploads/2025/07/ICON-2.png" alt="" />
                <div>
                  <div className="crm-social-fbcard-name">
                    {(preview.target_account_ids || []).map(id => accountName[id]).filter(Boolean).join(', ') || 'Your Page'}
                  </div>
                  <div className="crm-social-fbcard-meta">
                    {preview.published_at ? new Date(preview.published_at).toLocaleString() : 'Draft preview'} · 🌐
                  </div>
                </div>
              </div>
              <div className="crm-social-fbcard-body">
                <textarea
                  className="crm-social-edit-body"
                  rows={6}
                  value={edit.body}
                  onChange={e => setEdit({ ...edit, body: e.target.value })}
                  placeholder="Write the copy..."
                />
              </div>
              {edit.media_type === 'image' && edit.media_url && (
                <img className="crm-social-fbcard-media" src={edit.media_url} alt="post media" />
              )}
              {edit.media_type === 'video' && edit.media_url && (
                <video className="crm-social-fbcard-media" src={edit.media_url} controls />
              )}
              {edit.media_type === 'video' && !edit.media_url && preview.generation_meta && preview.generation_meta.storyboard && (
                <div className="crm-social-storyboard">
                  <div className="crm-social-storyboard-badge">Storyboard — held for human production</div>
                  {preview.generation_meta.storyboard.hook && (
                    <div className="crm-social-storyboard-hook">{preview.generation_meta.storyboard.hook}</div>
                  )}
                  <ol>
                    {(preview.generation_meta.storyboard.scenes || []).map((s, i) => (
                      <li key={i}>
                        <strong>{s.t || `Scene ${i + 1}`}:</strong> {s.visual}
                        {s.onscreen_text ? <em> — “{s.onscreen_text}”</em> : null}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              <div className="crm-social-edit-media">
                <input
                  value={edit.image_prompt}
                  onChange={e => setEdit({ ...edit, image_prompt: e.target.value })}
                  placeholder="Describe the image you want (e.g. a box truck at a loading dock)"
                />
                <button className="crm-btn crm-btn--sm crm-btn--ghost" disabled={editBusy} onClick={regenPreviewImage}>
                  {editBusy ? 'Working…' : edit.media_url ? 'Regenerate image' : 'Generate image'}
                </button>
              </div>
              <div className="crm-social-fbcard-link">
                {preview.link_url || 'gophrapp.com/?quote=1'} — a tracked link is appended automatically
              </div>
            </div>
            <div className="crm-social-preview-footer">
              <span className={`crm-social-status crm-social-status--${preview.status}`}>
                {STATUS_LABEL[preview.status] || preview.status}
              </span>
              <button className="crm-btn crm-btn--sm" disabled={editBusy} onClick={savePreviewEdits}>
                {editBusy ? 'Saving…' : 'Save changes'}
              </button>
              {livePermalink(preview) && (
                <a className="crm-btn crm-btn--sm" href={livePermalink(preview)} target="_blank" rel="noreferrer">View live post ↗</a>
              )}
              {['draft', 'needs_review', 'scheduled', 'failed', 'partially_published'].includes(preview.status) && (
                <button className="crm-btn crm-btn--sm crm-btn--ghost" disabled={busy} onClick={() => openSchedule(preview)}>
                  Schedule
                </button>
              )}
              {['draft', 'needs_review', 'scheduled', 'failed', 'partially_published'].includes(preview.status) && (
                <button
                  className="crm-btn crm-btn--sm"
                  disabled={busy}
                  onClick={() => openPublish(preview)}
                >
                  Publish now
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {scheduleTarget && (
        <div className="crm-social-preview-overlay" onClick={() => setScheduleTarget(null)}>
          <div className="crm-social-schedule" onClick={e => e.stopPropagation()}>
            <div className="crm-social-preview-head">
              <strong>Schedule post</strong>
              <button className="crm-icon-btn" onClick={() => setScheduleTarget(null)}>✕</button>
            </div>
            <p className="crm-social-hint">
              {scheduleTarget.title || (scheduleTarget.body || '').slice(0, 60) || 'Untitled'}
            </p>
            <label>
              Publish at
              <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} />
            </label>
            <div className="crm-social-sched-targets">
              <span className="crm-social-targets-label">Post to:</span>
              {connectedAccounts.length === 0 && (
                <span className="crm-social-hint">No accounts connected. Connect one below before scheduling.</span>
              )}
              {connectedAccounts.map(a => (
                <label key={a.crm_social_account_id} className="crm-social-chip">
                  <input
                    type="checkbox"
                    checked={scheduleAccountIds.includes(a.crm_social_account_id)}
                    onChange={() => setScheduleAccountIds(prev =>
                      prev.includes(a.crm_social_account_id)
                        ? prev.filter(x => x !== a.crm_social_account_id)
                        : [...prev, a.crm_social_account_id]
                    )}
                  />
                  {a.display_name || `${platformLabel(platforms, a.platform)} (${a.external_id})`}
                </label>
              ))}
            </div>
            {scheduleTarget.media_type === 'video' && (
              <p className="crm-social-hint">Video is held for review; it auto-publishes at this time once approved.</p>
            )}
            {scheduleError && <div className="crm-social-sched-error">{scheduleError}</div>}
            <div className="crm-social-actions">
              <button className="crm-btn crm-btn--ghost" onClick={() => setScheduleTarget(null)}>Cancel</button>
              <button className="crm-btn" disabled={busy} onClick={confirmSchedule}>
                {busy ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {publishTarget && (
        <div className="crm-social-preview-overlay" onClick={() => setPublishTarget(null)}>
          <div className="crm-social-schedule" onClick={e => e.stopPropagation()}>
            <div className="crm-social-preview-head">
              <strong>Publish now</strong>
              <button className="crm-icon-btn" onClick={() => setPublishTarget(null)}>✕</button>
            </div>
            <p className="crm-social-hint">
              {publishTarget.title || (publishTarget.body || '').slice(0, 60) || 'Untitled'}
            </p>
            <div className="crm-social-sched-targets">
              <span className="crm-social-targets-label">Publish to:</span>
              {connectedAccounts.length === 0 && (
                <span className="crm-social-hint">No accounts connected.</span>
              )}
              {connectedAccounts.map(a => (
                <label key={a.crm_social_account_id} className="crm-social-chip">
                  <input
                    type="checkbox"
                    checked={publishAccountIds.includes(a.crm_social_account_id)}
                    onChange={() => setPublishAccountIds(prev =>
                      prev.includes(a.crm_social_account_id)
                        ? prev.filter(x => x !== a.crm_social_account_id)
                        : [...prev, a.crm_social_account_id]
                    )}
                  />
                  {a.display_name || `${platformLabel(platforms, a.platform)} (${a.external_id})`}
                </label>
              ))}
            </div>
            {publishTarget.media_type === 'none' && publishAccountIds.some(id => {
              const acc = connectedAccounts.find(a => a.crm_social_account_id === id);
              return acc && acc.platform === 'instagram';
            }) && (
              <div className="crm-social-sched-error">Instagram needs an image or video. Add media or unselect Instagram.</div>
            )}
            {publishError && <div className="crm-social-sched-error">{publishError}</div>}
            <div className="crm-social-actions">
              <button className="crm-btn crm-btn--ghost" onClick={() => setPublishTarget(null)}>Cancel</button>
              <button className="crm-btn" disabled={busy} onClick={confirmPublish}>
                {busy ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="crm-card">
        <h3>Compose a post</h3>
        <p className="crm-social-hint">
          Write copy, attach an image or video, pick platforms, then publish now or schedule. Links are
          automatically UTM-tagged and tracked so clicks attribute to CRM contacts.
        </p>

        {meta && meta.ai_configured && (
          <div className="crm-social-ai">
            <div className="crm-social-ai-head">
              <strong>Generate with AI</strong>
              <span className="crm-social-ai-cost">Copy + media together, saved to Posts for review</span>
            </div>
            <div className="crm-field-row">
              <label>
                Pillar
                <select value={gen.pillar} onChange={e => setGen({ ...gen, pillar: e.target.value })}>
                  {((meta && meta.pillars) || []).map(p => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Draft for
                <select value={gen.platform} onChange={e => setGen({ ...gen, platform: e.target.value })}>
                  {platforms.map(p => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Media
                <select value={gen.media_type} onChange={e => setGen({ ...gen, media_type: e.target.value })}>
                  <option value="image">Image + copy</option>
                  <option value="video">Video + copy (storyboard)</option>
                  <option value="none">Copy only</option>
                </select>
              </label>
            </div>
            <div className="crm-field-row">
              <label className="crm-field-grow">
                Topic (optional)
                <input
                  value={gen.topic}
                  onChange={e => setGen({ ...gen, topic: e.target.value })}
                  placeholder="e.g. oilfield flatbed freight, missed pickups, GoPilot load verification"
                />
              </label>
            </div>
            <div className="crm-field-row">
              <label className="crm-field-grow">
                Instructions (optional)
                <input
                  value={gen.instructions}
                  onChange={e => setGen({ ...gen, instructions: e.target.value })}
                  placeholder="e.g. make it shorter and less promotional; use a real flatbed photo"
                />
              </label>
            </div>
            <div className="crm-social-actions">
              <button className="crm-btn" disabled={genBusy} onClick={generateDraft}>
                {genBusy ? 'Generating…' : 'Generate draft'}
              </button>
              {isAdmin && (
                <button className="crm-btn crm-btn--ghost" disabled={genBusy} onClick={runAutogen}>
                  {genBusy ? 'Working…' : `Top up this week's posts (${meta.autogen_buffer_target || meta.autogen_weekly_posts || 4})`}
                </button>
              )}
            </div>
            <p className="crm-social-hint">
              Generates the copy and the {gen.media_type === 'video' ? 'video (a real clip renders in the background)' : gen.media_type === 'image' ? 'image' : 'post'} together, then saves a draft to the Posts section below and opens a preview. Nothing publishes until you approve it.
              {meta.autogen_enabled
                ? ` Auto-generation is ON — about ${meta.autogen_weekly_posts || 4} posts/week are kept queued (one draft per idea, usable on all connected platforms) for you to review.`
                : ' Auto-fill tops up a small buffer of ready-to-review drafts on demand; set ENABLE_CRM_SOCIAL_AUTOGEN to run it daily.'}
            </p>
          </div>
        )}

        <div className="crm-field-row">
          <label>
            Internal title
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Optional label" />
          </label>
          <label>
            Media type
            <select value={form.media_type} onChange={e => setForm({ ...form, media_type: e.target.value })}>
              <option value="none">Text / link only</option>
              <option value="image">Image</option>
              <option value="video">Video (human review before auto-publish)</option>
            </select>
          </label>
        </div>
        <div className="crm-field-row">
          <label className="crm-field-grow">
            Copy
            <textarea
              rows={4}
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              placeholder="What do you want to say?"
            />
          </label>
        </div>
        <div className="crm-social-charcount">{charCount} characters</div>
        {form.media_type !== 'none' && (
          <div className="crm-field-row">
            <label className="crm-field-grow">
              {form.media_type === 'video' ? 'Video URL' : 'Image URL'}
              <input
                value={form.media_url}
                onChange={e => setForm({ ...form, media_url: e.target.value })}
                placeholder="https://..."
              />
            </label>
          </div>
        )}
        <div className="crm-field-row">
          <label className="crm-field-grow">
            Link / CTA destination
            <input
              value={form.link_url}
              onChange={e => setForm({ ...form, link_url: e.target.value })}
              placeholder="Defaults to the instant-quote page"
            />
          </label>
        </div>

        <label className="crm-social-capture">
          <input
            type="checkbox"
            checked={Boolean(form.capture_lead)}
            onChange={e => setForm({ ...form, capture_lead: e.target.checked })}
          />
          <span>
            Capture leads before the quote
            <small>Clicks land on a branded page that asks for name, email, phone, and company, then forwards to the quote. Submitters are added to the CRM as hot leads and stitched to their click history.</small>
          </span>
        </label>

        <div className="crm-social-targets">
          <span className="crm-social-targets-label">Post to:</span>
          {connectedAccounts.length === 0 && <span className="crm-social-hint">No accounts connected yet.</span>}
          {connectedAccounts.map(a => (
            <label key={a.crm_social_account_id} className="crm-social-chip">
              <input
                type="checkbox"
                checked={form.target_account_ids.includes(a.crm_social_account_id)}
                onChange={() => toggleTarget(a.crm_social_account_id)}
              />
              {a.display_name || `${platformLabel(platforms, a.platform)} (${a.external_id})`}
              {!a.is_connected && <span className="crm-social-badge crm-social-badge--warn">no token</span>}
            </label>
          ))}
        </div>

        <div className="crm-field-row">
          <label>
            Schedule for
            <input
              type="datetime-local"
              value={form.scheduled_for}
              onChange={e => setForm({ ...form, scheduled_for: e.target.value })}
            />
          </label>
        </div>

        <div className="crm-social-actions">
          <button className="crm-btn crm-btn--ghost" disabled={busy} onClick={saveDraft}>Save draft</button>
          <button className="crm-btn" disabled={busy} onClick={publishNow}>Publish now</button>
          <button className="crm-btn" disabled={busy} onClick={schedulePost}>Schedule</button>
        </div>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="crm-card">
          <div className="crm-social-analytics-head">
            <h3>Click attribution</h3>
            {analyticsPostId ? (
              <span className="crm-social-analytics-scope">
                Filtered to: <strong>{(posts.find(p => p.crm_social_post_id === analyticsPostId) || {}).title || 'selected post'}</strong>
                <button className="crm-btn crm-btn--sm crm-btn--ghost" onClick={() => setAnalyticsPostId(null)}>Show all</button>
              </span>
            ) : (
              <span className="crm-social-hint">Click a post below to see how that one is performing.</span>
            )}
          </div>
          <div className="crm-social-stats">
            <div className="crm-social-stat">
              <span className="crm-social-stat-value">{analytics.total_clicks || 0}</span>
              <span className="crm-social-stat-label">Total clicks</span>
            </div>
            <div className="crm-social-stat">
              <span className="crm-social-stat-value">{analytics.human_clicks || 0}</span>
              <span className="crm-social-stat-label">Human clicks</span>
            </div>
            <div className="crm-social-stat">
              <span className="crm-social-stat-value">{analytics.identified_clicks || 0}</span>
              <span className="crm-social-stat-label">Recognized CRM contacts</span>
            </div>
            <div className="crm-social-stat">
              <span className="crm-social-stat-value">{analytics.leads_captured || 0}</span>
              <span className="crm-social-stat-label">Leads captured</span>
            </div>
            <div className="crm-social-stat">
              <span className="crm-social-stat-value">{analytics.anonymous_clicks || 0}</span>
              <span className="crm-social-stat-label">Anonymous clicks</span>
            </div>
          </div>
          {analytics.by_platform && Object.keys(analytics.by_platform).length > 0 && (
            <div className="crm-social-byplatform">
              {Object.entries(analytics.by_platform).map(([p, v]) => (
                <span key={p} className="crm-social-badge">
                  {platformLabel(platforms, p)}: {v.human_clicks}/{v.clicks}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Posts */}
      <div className="crm-card">
        <h3>Posts</h3>
        {posts.length === 0 && <div className="crm-empty">No posts yet.</div>}
        {posts.length > 0 && (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Post</th>
                  <th>Platforms</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th>When</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr
                    key={post.crm_social_post_id}
                    className={`crm-social-post-row${analyticsPostId === post.crm_social_post_id ? ' crm-social-post-row--selected' : ''}`}
                    onClick={() => setAnalyticsPostId(post.crm_social_post_id)}
                    title="Click to filter click attribution to this post"
                  >
                    <td>
                      <strong className="crm-social-post-title">{post.title || (post.body || '').slice(0, 40) || 'Untitled'}</strong>
                      <small>{(post.body || '').slice(0, 80)}</small>
                    </td>
                    <td>
                      {(post.target_account_ids || []).map(id => (
                        <span key={id} className="crm-social-badge">{accountName[id] || 'account'}</span>
                      ))}
                    </td>
                    <td>
                      <span className="crm-social-cost" title={post.ai_generated ? 'AI generation cost' : 'Not AI-generated'}>
                        {formatCost(post.generation_cost_cents)}
                      </span>
                    </td>
                    <td>
                      <span className={`crm-social-status crm-social-status--${post.status}`}>
                        {STATUS_LABEL[post.status] || post.status}
                      </span>
                    </td>
                    <td>
                      {post.published_at
                        ? new Date(post.published_at).toLocaleString()
                        : post.scheduled_for
                        ? new Date(post.scheduled_for).toLocaleString()
                        : '—'}
                    </td>
                    <td>
                      <div className="crm-social-row-actions" onClick={e => e.stopPropagation()}>
                        <button className="crm-btn crm-btn--sm crm-btn--ghost" onClick={() => setPreview(post)}>
                          Preview
                        </button>
                        {['draft', 'needs_review', 'scheduled', 'failed', 'partially_published'].includes(post.status) && (
                          <button className="crm-btn crm-btn--sm crm-btn--ghost" disabled={busy} onClick={() => openSchedule(post)}>
                            Schedule
                          </button>
                        )}
                        {['draft', 'needs_review', 'scheduled', 'failed', 'partially_published'].includes(post.status) && (
                          <button className="crm-btn crm-btn--sm" disabled={busy} onClick={() => openPublish(post)}>
                            Publish
                          </button>
                        )}
                        <button className="crm-btn crm-btn--sm crm-btn--danger" disabled={busy} onClick={() => deletePost(post.crm_social_post_id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Accounts */}
      <div className="crm-card">
        <h3>Connected accounts</h3>
        {meta && meta.facebook_oauth_configured && isAdmin && (
          <div className="crm-social-connect-cta">
            <button className="crm-btn" disabled={busy} onClick={connectFacebook}>Connect Facebook &amp; Instagram</button>
            <span className="crm-social-hint">One click — authorize on Facebook and we auto-detect your Page and Instagram account.</span>
          </div>
        )}
        {isAdmin && (
          <div className="crm-social-token-connect">
            <label className="crm-social-hint" htmlFor="fb-token">Or paste a Facebook access token (we exchange it into a permanent Page + Instagram token):</label>
            <div className="crm-field-row">
              <input id="fb-token" className="crm-field-grow" type="password" value={fbToken} onChange={e => setFbToken(e.target.value)} placeholder="Facebook user or page access token" />
              <button className="crm-btn crm-btn--ghost" disabled={busy} onClick={connectFacebookByToken}>Connect with token</button>
            </div>
          </div>
        )}
        {accounts.length === 0 && <div className="crm-empty">No social accounts connected.</div>}
        {accounts.map(a => (
          <div key={a.crm_social_account_id} className="crm-social-account">
            <div>
              <strong>{a.display_name || platformLabel(platforms, a.platform)}</strong>
              <small>
                {platformLabel(platforms, a.platform)} · {a.external_id}
                {a.is_connected ? ' · token connected' : ' · no token'}
                {a.last_error ? ` · error: ${a.last_error}` : ''}
              </small>
            </div>
            {isAdmin && (
              <button className="crm-btn crm-btn--sm crm-btn--danger" disabled={busy} onClick={() => removeAccount(a.crm_social_account_id)}>
                Remove
              </button>
            )}
          </div>
        ))}

        {isAdmin && (
          <div className="crm-social-connect">
            <h4>Connect a page / organization</h4>
            <p className="crm-social-hint">
              Obtain a long-lived access token via the platform&apos;s OAuth flow, then paste it here. Facebook/Instagram
              use the Meta Graph API (page id + page access token; Instagram needs the IG business account id). LinkedIn
              uses the organization id with <code>w_organization_social</code>.
            </p>
            <div className="crm-field-row">
              <label>
                Platform
                <select value={accountForm.platform} onChange={e => setAccountForm({ ...accountForm, platform: e.target.value })}>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </label>
              <label>
                Page / Org / IG id
                <input value={accountForm.external_id} onChange={e => setAccountForm({ ...accountForm, external_id: e.target.value })} />
              </label>
              <label>
                Display name
                <input value={accountForm.display_name} onChange={e => setAccountForm({ ...accountForm, display_name: e.target.value })} />
              </label>
            </div>
            <div className="crm-field-row">
              <label className="crm-field-grow">
                Access token
                <input
                  type="password"
                  value={accountForm.access_token}
                  onChange={e => setAccountForm({ ...accountForm, access_token: e.target.value })}
                  placeholder="Long-lived page/org access token"
                />
              </label>
            </div>
            <button className="crm-btn" disabled={busy} onClick={connectAccount}>Save account</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialView;
